// use this Api as a function to make api calls;
import axiosInstance from '../axios';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { getTokenFromLocalStorage } from '../../helpers/localStorage';
import { ORGANIZATION } from '../../enums';

export const ORGANIZATION_WEB_COMMENT_SETTINGS_CHANGED = 'organization-web-comment-settings-changed';

export type OrganizationWebCommentSettings = {
  displayDeletedCommentThreads: boolean;
};

export const getOrganizationWebCommentSettings = async (
  organizationId: string,
): Promise<OrganizationWebCommentSettings> => {
  const config: CacheRequestConfig = { cache: false };
  const { data } = await axiosInstance.get<OrganizationWebCommentSettings>(
    `${API_ROUTES.ORGANIZATION}${organizationId}/web-comment-settings`,
    config,
  );
  return data;
};

export const patchOrganizationWebCommentSettings = async (
  organizationId: string | undefined,
  displayDeletedCommentThreads: boolean,
) => {
  const { data } = await axiosInstance.patch<OrganizationWebCommentSettings>(
    `${API_ROUTES.ORGANIZATION}${organizationId ? `${organizationId}/` : ''}web-comment-settings`,
    { displayDeletedCommentThreads },
  );
  window.dispatchEvent(new Event(ORGANIZATION_WEB_COMMENT_SETTINGS_CHANGED));
  return data;
};

export const getAllOrganizations = async (data?: {
  page: number;
  limit: number;
  search?: string;
  selectedType?: 'child' | 'parent' | 'all';
  deletion_status?: 'ACTIVE' | 'ARCHIVED';
  sort?: 'asc' | 'desc';
  sortField?:
    | 'name'
    | 'location'
    | 'total_users'
    | 'storage_usage'
    | 'hours_limit_used'
    | 'download_hours_limit_used'
    | 'period_end'
    | 'projected_hours_at_the_end'
    | 'created_at';
  ignoreOrgIds?: number[];
}) => {
  let type = '';
  if (data) {
    type = data?.selectedType === 'all' ? '' : `&type=${data?.selectedType}`;
  }

  const sortingQuery = `&sort=${data?.sort ? data.sort : 'asc'}&sortField=${data?.sortField ? data.sortField : 'name'}`;

  const ignoreOrgIdsQuery = data?.ignoreOrgIds?.length ? `&ignoreOrgIds=${data.ignoreOrgIds.join(',')}` : '';
  const statusQuery = data?.deletion_status ? `&deletion_status=${data.deletion_status}` : '';

  const query = data ? `?page=${data.page + 1}&limit=${data.limit}&search=${data.search}` : '';
  const config: CacheRequestConfig | undefined = data?.deletion_status ? { cache: false } : undefined;
  const res = await axiosInstance(`${API_ROUTES.ORGANIZATION}${query}${sortingQuery}${type}${ignoreOrgIdsQuery}${statusQuery}`, config);
  return res;
};

export const getAllChildOrganizations = async (data?: { id: number; page: number; limit: number; search?: string }) => {
  const query = data ? `/${data?.id}?page=${data.page + 1}&limit=${data.limit}&search=${data.search}` : '';
  const res = await axiosInstance(`${API_ROUTES.ORGANIZATION_CHILDS}${query}&sort=asc&sortField=name`);
  return res;
};

type TData = {
  name?: string;
  location?: string;
  description?: string;
  hours_limit?: number;
  users_limit?: number | null;
  storage_limit?: number | null;
  newUsers?: { email: string; username: string; role_id?: string; allow_download_own_composers?: boolean }[];
  users?: any[];
  ids?: { id: string }[];
};

export const patchOrganization = async (orgId: string, data: TData) => {
  const res = await axiosInstance.patch(`${API_ROUTES.ORGANIZATION}${orgId}`, data);
  return res;
};

type TpostOrganization = {
  name: string;
  location: string;
  users_limit: number | null;
  storage_limit: number | null;
  download_hours_limit?: number;
  admins: { email: string; username: string }[];
};

export const postOrganization = async (data: TpostOrganization) => {
  const res = await axiosInstance.post(`${API_ROUTES.ORGANIZATION}`, data);
  return res;
};

export const getUserOrganization = async (forceRefresh = false) => {
  const config: CacheRequestConfig | undefined = forceRefresh
    ? { cache: false }
    : undefined;
  const res = await axiosInstance.get(API_ROUTES.ORGANIZATION_MY_ORG, config);
  return res;
};

type TupdateUserOrganization = {
  name?: string;
  description?: string;
  newUsers?: {
    username: string;
    email: string;
    role_id: string;
    allow_download_own_composers?: boolean;
  }[];
};

export const updateUserOrganization = async (arg: TupdateUserOrganization) => {
  const res = await axiosInstance.patch(API_ROUTES.ORGANIZATION_MY_ORG, arg);
  return res;
};

export const getOrganizationUsers = async (data: {
  role: string | string[];
  page: number | null;
  limit: number | null;
  search?: string;
  sort?: string;
  sortField?: string;
  orgId?: string;
}) => {
  const { role, page, limit, search, sort, sortField, orgId } = data;
  let searchText = '';
  if (search?.toLocaleLowerCase().includes('+')) {
    searchText = search.replace(/\+/g, '%2B');
  } else if (search) {
    searchText = search;
  }
  let pageQuery = typeof page === 'number' && typeof limit === 'number' ? `page=${page + 1}&limit=${limit}` : '';
  const orgQuery = orgId ? `&org_id=${orgId}` : '';
  if (role && Array.isArray(role)) {
    const rolesQuery = role.map((r) => `&role=${r}`).join('');
    return await axiosInstance(
      `${API_ROUTES.ORGANIZATION_USERS}?${pageQuery}${rolesQuery}&sort=${sort}&sortField=${
        sortField ?? ''
      }&search=${searchText}${orgQuery}`,
    );
  }
  return await axiosInstance(
    `${API_ROUTES.ORGANIZATION_USERS}?${pageQuery}&role=${role}&sort=${sort}&sortField=${sortField ?? ''}&search=${searchText}${orgQuery}`,
  );
};

export const deleteOrganizationById = async (orgId: string, moveToTrash = true) => {
  const res = await axiosInstance.delete(`${API_ROUTES.ORGANIZATION}${orgId}`, { data: { moveToTrash } });
  return res;
};

export const restoreOrganizationById = async (orgId: string) => {
  return axiosInstance.post(`${API_ROUTES.ORGANIZATION}${orgId}/restore`);
};

export const getOrganizationUsersByRole = async (role: string | string[], search?: string) => {
  let link = `${API_ROUTES.ORGANIZATION_USERS}/?`;
  link += Array.isArray(role) ? role.map((el, i) => `&role[${i}]=${el}`).join('') : `role=${role}&`;
  link += `&search=${search ?? ''}`;
  const res = await axiosInstance(link);
  return res;
};

export const deleteOrgAvatar = async () => {
  const res = await axiosInstance.delete(API_ROUTES.ORGANIZATION_UPLOAD_AVATAR);
  return res;
};
export const uploadOrgAvatar = async (data: FormData) => {
  const res = await axiosInstance.patch(API_ROUTES.ORGANIZATION_UPLOAD_AVATAR, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res;
};

export const patchSwitchOrganizartion = async (organization_id?: string, asSuperAdmin?: boolean) => {
  if (asSuperAdmin) {
    return await axiosInstance.patch(API_ROUTES.SWITCH_ORG, { asSuperAdmin });
  }
  return await axiosInstance.patch(API_ROUTES.SWITCH_ORG, { organization_id });
};

export const patchUploadOrgAvatarById = async (orgId: string, data: FormData) => {
  const res = await axiosInstance.patch(`${API_ROUTES.ORGANIZATION_AVATAR}/${orgId}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res;
};

export const deleteOrgAvatarById = async (orgId: string) => {
  const res = await axiosInstance.delete(`${API_ROUTES.ORGANIZATION_AVATAR}/${orgId}`);
  return res;
};

export const getOrganizationRequest = async (orgId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.ORGANIZATION}${orgId}`);
  return res;
};

export const getOrganizationStorageUsage = async (orgId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.STORAGE_USAGE_BY_ORG_ID}/${orgId}`);
  return res;
};

export const patchOrganizationsRelations = async (
  orgId: string,
  data: {
    newOrganizations: number[];
    organizationsToRemove: number[];
  },
) => {
  const res = await axiosInstance.patch(`${API_ROUTES.ORGANIZATION}${orgId}/related`, data);
  return res;
};

export const postOranizationHoursUsageRefresh = async () => {
  const [uploadRes, downloadRes] = await Promise.all([
    axiosInstance.post(API_ROUTES.ORGANIZATION_HOURS_USAGE_REFRESH),
    axiosInstance.post(API_ROUTES.ORGANIZATION_DOWNLOAD_HOURS_USAGE_REFRESH),
  ]);
  return { uploadRes, downloadRes };
};

export async function getCSVforCurrentPeriodVideofeeds(org: ORGANIZATION) {
  const token = getTokenFromLocalStorage();
  const url = `${process.env.REACT_APP_API_URL}${API_ROUTES.ORGANIZATION_VIDEOFEED_CSV}${org.id}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'text/csv',
    },
    // credentials: 'include', // only if your auth uses cookies
  });

  if (!res.ok) {
    // try to surface server message if it’s JSON
    const ct = res.headers.get('content-type') || '';
    const msg = ct.includes('application/json') ? JSON.stringify(await res.json()) : await res.text();
    throw new Error(`CSV request failed (${res.status}): ${msg}`);
  }

  let filename = `${org.name} ${org.period_start}_${org.period_end}.csv`;

  const blob = await res.blob(); // <- IMPORTANT: don’t .json() or .text()
  const urlObject = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = urlObject;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(urlObject);
}
